import React, { useMemo, useState } from 'react';
import { Button, Loader, Message } from '@getro/rombo';
import { Box, Flex, Text } from 'rebass/styled-components';
import { activeNetworkSelector } from 'redux/selectors';
import { useSelector } from 'react-redux';
import { useGetSubscriptionsQuery } from 'services/organizationsV2';

import { SettingsLayoutHeader } from '../../../components/atoms/settingsLayoutHeader';
import UpgradeModal from '../../../components/organisms/upgradeModal';
import usePageHook from '../../../hooks/usePageHook';
import { BillingSupportContact } from './billingSupportContact';
import { PlansBox } from './plansBox';

const DEFAULT_BILLING_SUPPORT_EMAIL = 'billing@findem.ai';
const pageTitle = 'Plans and billing';

export const PlansAndBilling = () => {
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeFeatureName, setUpgradeFeatureName] = useState('contacts_limit');
  const network = useSelector(activeNetworkSelector);
  const { data: subscriptions, error, isLoading } = useGetSubscriptionsQuery(network.id);
  const displayedSubscriptions = useMemo(() => {
    if (!subscriptions?.activeSubscriptions) return [];
    return subscriptions.activeSubscriptions.filter((subscription) =>
      subscription.subscriptionItems?.some((item) => item.amount > 0),
    );
  }, [subscriptions]);

  const billingEligible = useMemo(() => {
    if (error) return true;
    if (isLoading) return true;
    return displayedSubscriptions.length > 0;
  }, [error, isLoading, displayedSubscriptions]);

  const billingSupportEmail = subscriptions?.billingSupportEmail || DEFAULT_BILLING_SUPPORT_EMAIL;

  const canAddCompanies = useMemo(() => {
    const sub = subscriptions?.activeSubscriptions?.find((subscription) =>
      subscription.subscriptionItems?.find((item) => item?.stairstep?.unit === 'companies'),
    ); // eslint-disable-line function-paren-newline

    if (sub) {
      const activeStairstep = sub.subscriptionItems?.find((item) => item?.stairstep?.unit === 'companies');

      if (activeStairstep) {
        return activeStairstep?.stairstep?.used < activeStairstep?.stairstep?.total;
      }
    }

    return true;
  }, [subscriptions]);

  usePageHook(pageTitle);

  if (!network.isManager) return null;

  const { connectTier } = network;

  const noConnectSubscription =
    !isLoading &&
    (error ||
      !subscriptions?.activeSubscriptions?.length ||
      !subscriptions?.activeSubscriptions?.find((subscription) =>
        subscription.subscriptionItems?.find(
          (item) => item?.itemPriceFamilyId === 'GetroConnect' || item?.stairstep?.unit === 'contacts',
        ),
      )); // eslint-disable-line function-paren-newline

  return (
    <Box maxWidth="600px">
      <SettingsLayoutHeader
        title={pageTitle}
        description="See your current plan and contact billing support for payment questions."
      />
      {isLoading && (
        <Flex mt="32px" justifyContent="center" width="100%">
          <Loader />
        </Flex>
      )}
      {!isLoading && !billingEligible && <BillingSupportContact email={billingSupportEmail} variant="unavailable" />}
      {billingEligible && (
        <>
          {noConnectSubscription && (
            <Box mt="32px">
              <PlansBox
                subscription={{
                  quantity: 1,
                  unitPrice: 0,
                  amount: 0,
                  freeQuantity: network.contactsLimit,
                  itemPriceFamilyId: 'GetroConnect',
                  stairstep: { unit: 'contacts', total: network.contactsLimit, used: network.contactsTotal },
                }}
                connectTier={connectTier}
                networkSlug={network.slug}
                billingPeriod={1}
                billingPeriodUnit="month"
                currencyCode="USD"
                upcomingInvoiceTotal={0}
                status="active"
              />
            </Box>
          )}
          {!isLoading && displayedSubscriptions.length > 0 && (
            <Flex mt="32px" sx={{ flexDirection: 'column', gap: '16px' }}>
              {displayedSubscriptions.map((subscription) => (
                <PlansBox
                  key={subscription.id}
                  subscription={subscription.subscriptionItems[0]}
                  connectTier={connectTier}
                  networkSlug={network.slug}
                  billingPeriod={subscription.billingPeriod}
                  billingPeriodUnit={subscription.billingPeriodUnit}
                  currencyCode={subscription.currencyCode}
                  upcomingInvoiceTotal={subscription.upcomingInvoiceTotal}
                  nextBillingAt={subscription.nextBillingAt}
                  coupon={subscription.coupon}
                  status={subscription.status}
                  currentTermEnd={subscription.currentTermEnd}
                />
              ))}
            </Flex>
          )}
          {network.contactsOverLimit > 0 && (
            <Message type="warning" mt="16px">
              <Text fontSize="14px" fontWeight="medium" color="text.dark">
                Your network has reached its contacts limit
              </Text>
              <Text fontSize="14px" mt="8px">
                Upgrade now to keep growing your network and make sure high-impact intros are not missed.
              </Text>
              <Button
                size="small"
                mt="8px"
                onClick={() => {
                  setUpgradeFeatureName('contacts_limit');
                  setShowUpgradeModal(true);
                }}
              >
                Upgrade contacts limit
              </Button>
            </Message>
          )}
          {!canAddCompanies && (
            <Message type="warning" mt="16px">
              <Text fontSize="14px" fontWeight="medium" color="text.dark">
                Your job board has reached its company limit
              </Text>
              <Text fontSize="14px" mt="8px">
                Upgrade now to keep growing your network and make sure high-impact intros are not missed.
              </Text>
              <Button
                size="small"
                mt="8px"
                onClick={() => {
                  setUpgradeFeatureName('companies_limit');
                  setShowUpgradeModal(true);
                }}
              >
                Upgrade company limit
              </Button>
            </Message>
          )}
          {!isLoading && <BillingSupportContact email={billingSupportEmail} variant="eligible" />}
        </>
      )}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        featureName={upgradeFeatureName}
      />
    </Box>
  );
};

export default PlansAndBilling;
